import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Modal, Box, Typography, Button, TextField, Autocomplete, CircularProgress, IconButton } from '@mui/material';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import PlaceIcon from '@mui/icons-material/Place';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { GOOGLE_MAPS_KEY } from '../../../../constants/constants';

type MapPickerModalProps = {
	open: boolean;
	onClose: () => void;
	onSelect: (lat: number, lng: number) => void;
	initialLat?: number;
	initialLng?: number;
	countryCodeToSearchPosition?: string;
};

const LIBRARIES: 'places'[] = ['places'];

const containerStyle = {
	width: '100%',
	height: '400px',
};

type Prediction = google.maps.places.AutocompletePrediction;

export const MapPickerModal: React.FC<MapPickerModalProps> = ({
	open,
	onClose,
	onSelect,
	initialLat = -34.9011, // Montevideo
	initialLng = -56.1645,
	countryCodeToSearchPosition,
}: MapPickerModalProps) => {
	const [marker, setMarker] = useState({ lat: initialLat, lng: initialLng });
	const [mapCenter, setMapCenter] = useState({ lat: initialLat, lng: initialLng });

	// Google Maps ready flags + services
	const [mapsReady, setMapsReady] = useState(false);
	const mapInstanceRef = useRef<google.maps.Map | null>(null);
	const autoServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
	const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);

	// Autocomplete state
	const [inputValue, setInputValue] = useState('');
	const [options, setOptions] = useState<Prediction[]>([]);
	const [loading, setLoading] = useState(false);
	const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | null>(null);

	// Reiniciar estado cuando se abre con nuevas coords
	useEffect(() => {
		if (open) {
			setMarker({ lat: initialLat, lng: initialLng });
			setMapCenter({ lat: initialLat, lng: initialLng });
			setInputValue('');
			setOptions([]);
			setSessionToken(null);
		}
	}, [open, initialLat, initialLng]);

	// Crea servicios cuando Maps está listo
	const handleMapsLoad = useCallback(() => {
		setMapsReady(true);
	}, []);

	const handleMapOnLoad = useCallback((map: google.maps.Map) => {
		mapInstanceRef.current = map;
		// PlacesService necesita un nodo o el propio mapa
		placesServiceRef.current = new google.maps.places.PlacesService(map);
		autoServiceRef.current = new google.maps.places.AutocompleteService();
		setSessionToken(new google.maps.places.AutocompleteSessionToken());
	}, []);

	// Buscar predicciones (con debounce simple)
	const debouncedFetch = useMemo(() => {
		let t: number | null = null;
		return (q: string) => {
			if (!autoServiceRef.current) return;
			if (t) window.clearTimeout(t);
			t = window.setTimeout(() => {
				setLoading(true);
				autoServiceRef?.current?.getPlacePredictions(
					{
						input: q,
						sessionToken: sessionToken || undefined,
						componentRestrictions: { country: countryCodeToSearchPosition ? [countryCodeToSearchPosition] : [] },
					},
					(preds) => {
						setOptions(preds || []);
						setLoading(false);
					},
				);
			}, 250);
		};
	}, [sessionToken]);

	const handleInputChange = (_: any, value: string) => {
		setInputValue(value);
		if (!value.trim()) {
			setOptions([]);
			return;
		}
		if (mapsReady) debouncedFetch(value);
	};

	const handleOptionSelect = async (_: any, prediction: Prediction | null) => {
		if (!prediction) return;
		if (!placesServiceRef.current) return;
		try {
			await new Promise<void>((resolve, reject) => {
				placesServiceRef?.current?.getDetails(
					{
						placeId: prediction.place_id,
						fields: ['geometry', 'name', 'formatted_address'],
						sessionToken: sessionToken || undefined,
					},
					(place, status) => {
						if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.geometry?.location) {
							reject(status);
							return;
						}
						const lat = place.geometry.location.lat();
						const lng = place.geometry.location.lng();
						setMarker({ lat, lng });
						setMapCenter({ lat, lng });
						resolve();
					},
				);
			});
			// Nueva sesión después de una selección
			setSessionToken(new google.maps.places.AutocompleteSessionToken());
		} catch {
			// noop
		}
	};

	const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
		if (e.latLng) {
			const lat = e.latLng.lat();
			const lng = e.latLng.lng();
			setMarker({ lat, lng });
			setMapCenter({ lat, lng });
		}
	}, []);

	const handleConfirm = () => {
		onSelect(marker.lat, marker.lng);
		onClose();
	};

	const recenterToMarker = () => {
		if (mapInstanceRef.current) {
			mapInstanceRef.current.panTo(marker);
			mapInstanceRef.current.setZoom(15);
		}
	};

	if (!GOOGLE_MAPS_KEY) {
		console.error('Falta definir GOOGLE_MAPS_KEY en constants/constants.ts');
		return null;
	}

	return (
		<Modal
			open={open}
			onClose={onClose}
			// Claves para permitir foco libre (sin trap) dentro del Modal
			disableEnforceFocus
			disableAutoFocus
			keepMounted
		>
			<Box
				sx={{
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					width: '90%',
					maxWidth: 700,
					bgcolor: 'background.paper',
					boxShadow: 24,
					p: 2,
					borderRadius: 2,
				}}
			>
				<Typography variant="h6" gutterBottom>
					Seleccionar ubicación
				</Typography>

				<LoadScript googleMapsApiKey={GOOGLE_MAPS_KEY} libraries={LIBRARIES} onLoad={handleMapsLoad}>
					{/* Barra de búsqueda con estilo MUI */}
					<Box
						mb={2}
						sx={{
							position: 'relative',
							display: 'flex',
							alignItems: 'center',
							border: 1,
							borderColor: 'divider',
							borderRadius: 1,
							px: 1,
							py: 0.5,
							'&:focus-within': (t) => ({
								borderColor: t.palette.primary.main,
								boxShadow: `0 0 0 3px ${t.palette.primary.main}22`,
							}),
							minHeight: 56,
							gap: 1,
							zIndex: 10,
						}}
					>
						<PlaceIcon sx={{ color: 'text.secondary', flex: '0 0 auto' }} />
						<Autocomplete
							fullWidth
							freeSolo
							loading={loading}
							options={options}
							getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.description)}
							filterOptions={(x) => x} // no filtres en cliente; confiá en Places
							onInputChange={handleInputChange}
							inputValue={inputValue}
							onChange={handleOptionSelect}
							renderInput={(params) => (
								<TextField
									{...params}
									placeholder="Buscar dirección o lugar…"
									variant="outlined"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{loading ? <CircularProgress color="inherit" size={18} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
							renderOption={(props, option) => (
								<li {...props} key={option.place_id}>
									{option.description}
								</li>
							)}
						/>
						<IconButton onClick={recenterToMarker} title="Centrar en marcador" size="small">
							<MyLocationIcon fontSize="small" />
						</IconButton>
					</Box>

					<GoogleMap
						mapContainerStyle={containerStyle}
						center={mapCenter}
						zoom={15}
						onLoad={handleMapOnLoad}
						onClick={handleMapClick}
					>
						<Marker position={marker} />
					</GoogleMap>
				</LoadScript>

				<Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
					<Button onClick={onClose} color="inherit">
						Cancelar
					</Button>
					<Button onClick={handleConfirm} variant="contained">
						Confirmar
					</Button>
				</Box>
			</Box>
		</Modal>
	);
};
